/**
 * aerialComposite — applies the baked Hillaire aerial-perspective froxel
 * volume (AerialPerspectiveLut) to the shaded scene as a post pass:
 *
 *   out = sceneColor · ap.a + ap.rgb        (per froxel sample)
 *
 * The froxel W coordinate is the view DISTANCE (not raw depth): view-space Z
 * from the hardware depth via perspectiveDepthToViewZ, corrected by the
 * per-pixel secant (rays toward screen corners travel farther per unit depth)
 * so the haze is radially consistent, then converted world-units → km and
 * normalized by the volume's max distance. Sky pixels (depth ≈ far plane) are
 * masked out — the sky-view LUT already contains full-path scattering; adding
 * the froxel's would double-count.
 *
 * This closes the "baked but never composited" gap (2026-08-17 shader audit):
 * SkyLutSet bakes the volume behind `enableAerialPerspective`; this node is
 * the consumer, wired via PostProcessingPipeline's `aerial` config.
 */

import type * as THREE from 'three/webgpu';
import { Matrix4, Vector3 } from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, mix, clamp,
  texture3D, perspectiveDepthToViewZ,
  normalize, sqrt, acos, asin, sign, abs, select, max,
} from 'three/tsl';
import { depthScalar, skyMask } from '../../depth/skyMask.js';

export interface AerialCompositeConfig {
  /** The baked froxel volume (SkyLutSet.aerial.texture). */
  volume: THREE.Data3DTexture;
  /** Max froxel distance in km — must match the bake (default 32). */
  maxDistanceKm?: number;
  /** World-unit → km conversion (default 0.001: metres). */
  kmPerWorldUnit?: number;
  /** Effect strength 0..1 (default 1). */
  intensity?: number;
  enabled?: boolean;
  /**
   * The volume's direction frame (AerialPerspectiveLut's AerialFrame — it must match the bake). 'world' looks the
   * pixel's WORLD ray up (cameraWorld + sunDirection, written per frame); 'screen' is the legacy screen-uv lookup.
   * Live: `uniforms.worldFrame` (0 / 1). Default 'screen'.
   */
  frame?: 'screen' | 'world';
}

export interface AerialCompositeUniforms {
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  maxDistanceKm: { value: number };
  kmPerWorldUnit: { value: number };
  intensity: { value: number };
  /**
   * Multiplies the baked in-scatter (2026-09-25): a sky whose LUTs bake at
   * sunIlluminance 1 carries its live, exposure-scaled illuminance here — the
   * same factor as the sky node's radianceScale — so the haze on geometry
   * matches the sky behind it without a froxel re-bake. Default 1.
   */
  inscatterScale: { value: number };
  /** The scene camera's world matrix (the post quad's own camera is not it) — write every frame for 'world'. */
  cameraWorld: { value: THREE.Matrix4 };
  /** The world sun direction (unit) — write every frame for 'world'. */
  sunDirection: { value: THREE.Vector3 };
  /** 1 = the 'world' lookup, 0 = the legacy screen-uv one (a live A/B; must match the volume's bake frame). */
  worldFrame: { value: number };
  /**
   * THE NIGHT IN-SCATTER (2026-10-06): the volume bakes the SUN's in-scatter only — black at night — so a clear night's
   * distant ridge faded to BLACK instead of the night horizon behind it. The caller writes the night horizon's radiance
   * here (scene units, RGB; an isotropic medium lit by the night sky converges to it), added × (1 − the froxel's
   * transmittance). Default 0 = the old composite bit for bit. `aerialNightCpu` is the mirror.
   */
  nightInscatter: { value: THREE.Vector3 };
}

/** CPU mirror of one pixel of the composite with THE NIGHT IN-SCATTER (exported for tests). */
export function aerialNightCpu(scene: number, apA: number, apRgb: number, night: number, intensity = 1, inscatterScale = 1): number {
  const trans = 1 + (apA - 1) * intensity;
  return scene * trans + apRgb * intensity * inscatterScale + night * (1 - trans);
}

/** CPU mirror of the froxel-W computation (exported for tests). */
export function froxelWCpu(
  viewZ: number, ndcX: number, ndcY: number,
  tanHalfFovY: number, aspect: number,
  kmPerWorldUnit: number, maxDistanceKm: number,
): number {
  const rx = ndcX * tanHalfFovY * aspect;
  const ry = ndcY * tanHalfFovY;
  const secant = Math.sqrt(rx * rx + ry * ry + 1);
  const distKm = -viewZ * secant * kmPerWorldUnit;
  const w = distKm / maxDistanceKm;
  return w <= 0 ? 0 : (w > 1 ? 1 : w); // <=: viewZ 0 yields -0, normalize it
}

export function createAerialCompositeNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config: AerialCompositeConfig,
): { node: unknown; uniforms: AerialCompositeUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);
  const uMaxKm = uniform(config.maxDistanceKm ?? 32);
  const uKmPerUnit = uniform(config.kmPerWorldUnit ?? 0.001);
  const uIntensity = uniform(config.intensity ?? 1);
  const uInscatterScale = uniform(1);
  const uCamWorld = uniform(new Matrix4());
  const uSunDir = uniform(new Vector3(0, 1, 0));
  const uWorldFrame = uniform(config.frame === 'world' ? 1 : 0);
  const uNightInscatter = uniform(new Vector3(0, 0, 0));

  const build = Fn(() => {
    const c = vec4(input as never);
    // Scalar depth (the `.r` reduction — same convention as GodRays/clouds).
    // THE LAZY-SAMPLE LAW (2026-09-29, the height fog's bug): the depth sample is a VAR at the top of the function, so
    // its one emission can never land inside the `select` below's branch and leave the other path reading it unset.
    const depth = float(depthScalar(depthNode) as never).toVar() as never;
    const viewZ = perspectiveDepthToViewZ(depth, uNear, uFar).toVar(); // negative metres
    const ndc = uv().mul(2).sub(vec2(1, 1));
    const ray = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(1));
    const distKm = viewZ.negate().mul(ray.length()).mul(uKmPerUnit);
    const w = clamp(distKm.div(uMaxKm), float(0), float(1));
    // THE AERIAL FRAME (sky/post 2026-09-27): 'world' — the pixel's WORLD ray (uv() is top-origin: NDC y = 1 − 2v),
    // its azimuth from the sun's vertical plane and its horizon-packed elevation: the TSL mirror of
    // aerialUvForDirection. 'screen' — the legacy lookup at the screen uv (a fixed pyramid, mirrored in y).
    const rayView = vec3(ndc.x.mul(uTanHalf).mul(uAspect), float(1).sub(uv().y.mul(2)).mul(uTanHalf), float(-1));
    const rd = normalize(uCamWorld.mul(vec4(rayView, 0)).xyz);
    const hs = sqrt(rd.x.mul(rd.x).add(rd.z.mul(rd.z))).mul(sqrt(uSunDir.x.mul(uSunDir.x).add(uSunDir.z.mul(uSunDir.z))));
    const cosPhi = select(hs.greaterThan(1e-6), rd.x.mul(uSunDir.x).add(rd.z.mul(uSunDir.z)).div(max(hs, float(1e-6))), float(1));
    const uW = acos(clamp(cosPhi, float(-1), float(1))).div(Math.PI);
    const th = asin(clamp(rd.y, float(-1), float(1)));
    const vW = float(0.5).add(float(0.5).mul(sign(th)).mul(sqrt(abs(th).div(Math.PI / 2))));
    const apUv = mix(vec2(uv().x, uv().y), vec2(uW, vW), uWorldFrame);
    const ap = texture3D(config.volume, vec3(apUv.x, apUv.y, w));
    const trans = mix(float(1), ap.a, uIntensity);
    const inscatter = ap.rgb.mul(uIntensity).mul(uInscatterScale).add(uNightInscatter.mul(float(1).sub(trans)));
    const composited = c.rgb.mul(trans).add(inscatter);
    // Sky pixels keep the sky-view result (it already carries full-path
    // scattering; adding the froxel's would double-count). WHY the helper: the
    // old `step(0.999, depth)` is z ≈ near/0.001 = 50 m on this camera, so it
    // masked the haze off everything BEYOND 50 m — the effect inverted. See
    // render/depth/skyMask.ts.
    return vec4(mix(composited, c.rgb, skyMask(depth)), c.a);
  });

  return {
    node: build(),
    uniforms: {
      near: uNear as unknown as { value: number },
      far: uFar as unknown as { value: number },
      tanHalfFovY: uTanHalf as unknown as { value: number },
      aspect: uAspect as unknown as { value: number },
      maxDistanceKm: uMaxKm as unknown as { value: number },
      kmPerWorldUnit: uKmPerUnit as unknown as { value: number },
      intensity: uIntensity as unknown as { value: number },
      inscatterScale: uInscatterScale as unknown as { value: number },
      cameraWorld: uCamWorld as unknown as { value: THREE.Matrix4 },
      sunDirection: uSunDir as unknown as { value: THREE.Vector3 },
      worldFrame: uWorldFrame as unknown as { value: number },
      nightInscatter: uNightInscatter as unknown as { value: THREE.Vector3 },
    },
  };
}
